import { ACCOUNT_NAME_MSG, ADD_COMPANY_MSG, MSG, DEFAULT_INDUSTRY, PRODUCTS, type Industry, type Me, type Product } from "@genbaryoku/contract";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { api, ApiError, messageOf } from "../../api.js";
import { setMe } from "../../auth.js";
import { ErrorText, Field, Help, HiddenUsername, PasswordInput, guardComposingEnter, useFieldIds } from "../../components/ui.jsx";
import { IndustrySelect } from "../../components/IndustrySelect.jsx";
import { t } from "../../labels.js";
import { useProduct } from "../../lib/useProduct.js";

/**
 * E0-c：同じアカウントで、この製品（いまのドメイン）の会社を作る（土台。../eigyouryoku/docs/02 決めごと17、E1_実装依頼.md §0 の4・5）。
 *   ・会員登録で exists（mode="signup"）：「既に別のサービスで利用されています…」→ いまのパスワードを入れて「営業力の会社を作成」
 *   ・ログインで noCompany（mode="login"）：「「営業力」の会社がまだありません。」→ 同じメール・パスワードで「営業力の会社を作成」＋ほかの製品の案内
 *   作ったら そのままホーム（会社名は初回の発行で聞く＝今の作法）。会社のデータは写らない
 *   氏名（#152、docs/61）：人に氏名があれば聞かない（API が写す）。会員登録から来たら会員登録の姓・名を送る。
 *   ログインから来て人に氏名が無ければ API が 400 ACCOUNT_NAME_REQUIRED → 姓・名の欄を出して送り直す
 */
export function AddCompanyPanel({
  mode,
  email,
  password,
  otherProducts = [],
  onBack,
  industry: industryFirst,
  name,
}: {
  mode: "signup" | "login";
  email: string;
  /** ログインで打ったパスワード（signup のときは画面で聞く） */
  password?: string;
  otherProducts?: Product[];
  onBack: () => void;
  /** 会員登録の画面で選んだ業種（#142 段4）。無ければ製品の既定 */
  industry?: Industry;
  /** 会員登録の画面で入れた姓・名（#152）。人に氏名が無ければこれが人の氏名になる */
  name?: { familyName: string; givenName: string };
}) {
  const ids = useFieldIds(["pw", "industry", "family", "given"] as const);
  const P = useProduct();
  const [industry, setIndustry] = useState<Industry>(industryFirst ?? DEFAULT_INDUSTRY[P.product]);
  const nav = useNavigate();
  const qc = useQueryClient();
  const [pw, setPw] = useState(password ?? "");
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  /** 人に氏名が無かった（ACCOUNT_NAME_REQUIRED）：姓・名の欄を出す */
  const [askName, setAskName] = useState(false);
  const [family, setFamily] = useState(name?.familyName ?? "");
  const [given, setGiven] = useState(name?.givenName ?? "");
  const create = async () => {
    setError(undefined);
    if (!pw) {
      setError("パスワードを入れてください");
      return;
    }
    setBusy(true);
    try {
      const person = family.trim() && given.trim() ? { familyName: family, givenName: given } : {};
      const me = await api<Me>("POST", "/auth/companies", { email, password: pw, industry, ...person });
      await setMe(qc, me);
      nav("/home", { replace: true, state: { justRegistered: true } });
    } catch (e) {
      if (e instanceof ApiError && e.code === "ACCOUNT_NAME_REQUIRED") setAskName(true);
      setError(messageOf(e));
      setBusy(false);
    }
  };
  return (
    <div className="addco" data-testid="add-company">
      <p style={{ margin: 0 }}>
        <b data-testid="add-company-lead">{t(mode === "signup" ? ADD_COMPANY_MSG.signupExists : ADD_COMPANY_MSG.noCompany)}</b>
        {mode === "signup" ? <Help testId="help-add-company" text={t(ADD_COMPANY_MSG.signupExistsHint)} /> : null}
      </p>
      {mode === "signup" ? (
        <>
          <HiddenUsername email={email} />
          <Field id={ids.pw} label={`パスワード（${email} の現在のパスワード）`}>
            <PasswordInput id={ids.pw} autoComplete="current-password" value={pw} onChange={setPw} />
          </Field>
        </>
      ) : null}
      {askName ? (
        <div className="namerow" data-testid="add-company-name">
          <Field id={ids.family} label={ACCOUNT_NAME_MSG.family}>
            <input id={ids.family} className="inp" autoComplete="family-name" maxLength={50} value={family} onKeyDown={guardComposingEnter} onChange={(e) => setFamily(e.target.value)} />
          </Field>
          <Field id={ids.given} label={ACCOUNT_NAME_MSG.given}>
            <input id={ids.given} className="inp" autoComplete="given-name" maxLength={50} value={given} onKeyDown={guardComposingEnter} onChange={(e) => setGiven(e.target.value)} />
          </Field>
        </div>
      ) : null}
      <IndustrySelect id={ids.industry} product={P.product} value={industry} onChange={setIndustry} />
      <ErrorText>{error}</ErrorText>
      <button type="button" className="btn primary big" disabled={busy} onClick={() => void create()} data-testid="add-company-create">
        {t(ADD_COMPANY_MSG.create)}
      </button>
      {otherProducts.map((p) => (
        <p key={p} className="hint" style={{ margin: 0, textAlign: "center" }} data-testid="add-company-other">
          <a className="btn text" href={`https://${PRODUCTS[p].host}/login`}>{ADD_COMPANY_MSG.otherProduct(PRODUCTS[p].name, PRODUCTS[p].host)}</a>
        </p>
      ))}
      <div className="authlinks">
        {mode === "signup" ? <Link className="btn text" to="/forgot">{MSG.forgotLink}</Link> : null}
        <button type="button" className="btn text" onClick={onBack}>
          {mode === "signup" ? "別のメールアドレスで登録" : "別のメールアドレスでログイン"}
        </button>
      </div>
    </div>
  );
}
